import BusinessIcon from '@mui/icons-material/Business';
import Diversity1Icon from '@mui/icons-material/Diversity1';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SavingsIcon from '@mui/icons-material/Savings';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import type { SvgIconComponent } from '@mui/icons-material';
import type { TEntityChoice } from '../../EntityTypeSelector/EntityTypeSelector/entityTypeGroups';

export type EntityTileContent = {
  Icon: SvgIconComponent;
  title: string;
  description: string;
  hint: string;
};

/**
 * What each entity type tile says, as built. Every string and icon is the one in the type's own
 * tile in EntityTypeSelector (SoleTraderTile, PartnershipTile, PrivateCompanyTile,
 * PublicCompanyTile, SmsfTile, UnregulatedTrustTile, RegulatedTrustTile), and for `individual`,
 * the Individual tile's (IndividualTile), which the choose screen offers as "just me" (verifime-adr#709).
 * Those tiles hard-code their copy inside their component and cannot be a radio, so the choose
 * screen's own tile reads the same words from here. entityTileContent.test.tsx renders each
 * as-built tile beside the choose screen's and fails when the two differ.
 */
export const ENTITY_TILE_CONTENT: Record<TEntityChoice, EntityTileContent> = {
  individual: {
    Icon: PersonIcon,
    title: 'Individual',
    description: 'Just you as a person verifying your own identity.',
    hint: "Choose this if you're signing up in your own name and not for a business or trust.",
  },
  Sole_Trader: {
    Icon: WorkIcon,
    title: 'Sole Trader',
    description:
      "You run a business in your own name (or a business name) and you're personally responsible for it.",
    hint: "Choose this if you're a freelancer, contractor, or small business owner working for yourself.",
  },
  Partnership: {
    Icon: HandshakeIcon,
    title: 'Partnership',
    description:
      'Two or more people or businesses run a business together and share profits and responsibilities.',
    hint: 'Choose this if your business is formally run by two or more partners.',
  },
  Private_Company: {
    Icon: BusinessIcon,
    title: 'Private Company',
    description:
      'A business with “Pty Ltd” in its name, owned by a small group of people. It can’t sell shares to the public.',
    hint: 'Choose this if your business is a registered Pty Ltd company.',
  },
  Public_Company: {
    Icon: BusinessIcon,
    title: 'Public Company',
    description:
      'A business with “Ltd” in its name that can sell shares to the public, often listed on a stock exchange.',
    hint: 'Choose this if your company is publicly listed or raising funds from the public.',
  },
  SMSF: {
    Icon: SavingsIcon,
    // COPY APPROVED (verifime-adr#683): Zoe McKeough, 2026-11-04
    title: 'Self-Managed Super Fund',
    description: 'A private super fund you set up and manage yourself for your retirement savings.',
    hint: "Choose this if you're verifying a SMSF with up to six members.",
  },
  Trust_Unregulated: {
    Icon: Diversity1Icon,
    title: 'Unregulated Trust',
    description:
      'A trust used to hold and manage assets for family or others, not supervised by government regulators.',
    hint: 'Choose this if you have a family trust, discretionary trust, or similar arrangement.',
  },
  Trust_Regulated: {
    Icon: Diversity1Icon,
    title: 'Regulated Trust',
    description:
      'A trust overseen by regulators like ASIC or APRA, usually for managed investments.',
    hint: 'Choose this if you manage a registered investment trust or managed investment scheme.',
  },
};
